import React from 'react';
import { Play } from 'lucide-react';
import { isMobile, isTablet } from '../../../shared/utils/deviceUtils';

export const VideoSection: React.FC = () => {
  const mobile = isMobile();
  const tablet = isTablet();

  return (
    <section
      className={`${mobile ? 'py-16' : tablet ? 'py-20' : 'py-24'} bg-surface-primary`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {mobile ? (
          // Mobile layout: Text content, then video, then button
          <div className="space-y-6">
            <div className="p-4 sm:p-6 pb-0 lg:p-8">
              <h2
                className={`${mobile ? 'text-3xl' : tablet ? 'text-4xl' : 'text-4xl'} font-bold text-text-primary mb-4 sm:mb-6`}
              >
                Experience the Wild Like Never Before
              </h2>
              <p
                className={`${mobile ? 'text-base' : 'text-lg'} text-text-secondary mb-6 sm:mb-8 leading-relaxed`}
              >
                Join thousands of adventurers who have discovered the magic of
                African wildlife. Our expert guides and carefully planned
                itineraries ensure you get the most out of your safari experience.
              </p>
              <div
                className={`flex ${mobile ? 'flex-row' : 'items-center'} ${mobile ? 'gap-4 mb-6' : 'gap-8 mb-8'}`}
              >
                <div className="text-left">
                  <div
                    className={`${mobile ? 'text-2xl' : 'text-3xl'} font-bold text-primary-500`}
                  >
                    15K+
                  </div>
                  <div
                    className={`${mobile ? 'text-xs' : 'text-sm'} text-text-tertiary`}
                  >
                    Happy Travelers
                  </div>
                </div>
                <div className="text-left">
                  <div
                    className={`${mobile ? 'text-2xl' : 'text-3xl'} font-bold text-primary-500`}
                  >
                    98%
                  </div>
                  <div
                    className={`${mobile ? 'text-xs' : 'text-sm'} text-text-tertiary`}
                  >
                    Satisfaction Rate
                  </div>
                </div>
                <div className="text-left">
                  <div
                    className={`${mobile ? 'text-2xl' : 'text-3xl'} font-bold text-primary-500`}
                  >
                    200+
                  </div>
                  <div
                    className={`${mobile ? 'text-xs' : 'text-sm'} text-text-tertiary`}
                  >
                    Expert Guides
                  </div>
                </div>
              </div>
            </div>

            {/* Video card positioned between stats and button */}
            <div className="p-4">
              <div className="relative rounded-xl overflow-hidden shadow-large">
                <img
                  src="https://images.pexels.com/photos/4577791/pexels-photo-4577791.jpeg"
                  alt="Safari experience"
                  className="w-full h-48 object-cover"
                />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <button
                    className="w-12 h-12 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center hover:bg-white transition-colors group"
                  >
                    <Play
                      className="w-4 h-4 text-primary-500 ml-1 group-hover:scale-110 transition-transform"
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Button positioned after video */}
            <div className="p-4 sm:p-6 lg:p-8 pt-0">
              <button
                className={`${mobile ? 'px-6 py-3 text-sm' : 'px-8 py-4'} bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-all duration-200 font-medium shadow-soft hover:shadow-primary transform hover:scale-105`}
              >
                Start Your Adventure
              </button>
            </div>
          </div>
        ) : (
          // Desktop/tablet layout: Side by side
          <div
            className={`grid ${tablet ? 'grid-cols-1 lg:grid-cols-2 gap-12' : 'grid-cols-1 lg:grid-cols-2 gap-16'} items-start`}
          >
            <div className="p-4 sm:p-6 lg:p-8">
              <h2
                className={`${mobile ? 'text-3xl' : tablet ? 'text-4xl' : 'text-4xl'} font-bold text-text-primary mb-4 sm:mb-6`}
              >
                Experience the Wild Like Never Before
              </h2>
              <p
                className={`${mobile ? 'text-base' : 'text-lg'} text-text-secondary mb-6 sm:mb-8 leading-relaxed`}
              >
                Join thousands of adventurers who have discovered the magic of
                African wildlife. Our expert guides and carefully planned
                itineraries ensure you get the most out of your safari experience.
              </p>
              <div
                className={`flex ${mobile ? 'flex-row' : 'items-center'} ${mobile ? 'gap-4 mb-6' : 'gap-8 mb-8'}`}
              >
                <div className="text-center">
                  <div
                    className={`${mobile ? 'text-2xl' : 'text-3xl'} font-bold text-primary-500`}
                  >
                    15K+
                  </div>
                  <div
                    className={`${mobile ? 'text-xs' : 'text-sm'} text-text-tertiary`}
                  >
                    Happy Travelers
                  </div>
                </div>
                <div className="text-center">
                  <div
                    className={`${mobile ? 'text-2xl' : 'text-3xl'} font-bold text-primary-500`}
                  >
                    98%
                  </div>
                  <div
                    className={`${mobile ? 'text-xs' : 'text-sm'} text-text-tertiary`}
                  >
                    Satisfaction Rate
                  </div>
                </div>
                <div className="text-center">
                  <div
                    className={`${mobile ? 'text-2xl' : 'text-3xl'} font-bold text-primary-500`}
                  >
                    200+
                  </div>
                  <div
                    className={`${mobile ? 'text-xs' : 'text-sm'} text-text-tertiary`}
                  >
                    Expert Guides
                  </div>
                </div>
              </div>
              <button
                className={`${mobile ? 'px-6 py-3 text-sm' : 'px-8 py-4'} bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-all duration-200 font-medium shadow-soft hover:shadow-primary transform hover:scale-105`}
              >
                Start Your Adventure
              </button>
            </div>

            <div className="p-4 sm:p-6 lg:p-8">
              <div className="relative rounded-xl overflow-hidden shadow-large">
                <img
                  src="https://images.pexels.com/photos/4577791/pexels-photo-4577791.jpeg"
                  alt="Safari experience"
                  className={`w-full ${tablet ? 'h-80' : 'h-96'} object-cover`}
                />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <button
                    className="w-20 h-20 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors group"
                  >
                    <Play
                      className="w-8 h-8 text-primary-500 ml-1 group-hover:scale-110 transition-transform"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
